/**
 * DeckProbabilityPanel - Right panel for probability tools
 *
 * Displays sample hand simulator, batch simulation, and probability calculator
 * in a dedicated panel that shares the right column slot with CardDiscoveryPanel.
 *
 * Owns the simulation settings shared by every tool below it, so the sample hand
 * and the batch run can never disagree about what "keepable" means.
 */
import { useAtom } from 'jotai';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { IoCalculator, IoChevronDown, IoChevronForward, IoDice, IoOptions, IoStatsChart } from 'react-icons/io5';

import type { KeepRules, TurnSimOptions } from '@/features/decks/lib/handSim';
import { handSimSettingsAtom, resolveKeepRules, resolveTurnOptions } from '@/features/decks/state/handSimSettings';
import type { DeckFormat } from '@/types/deck';

import type { EnrichedDeckCard } from '../types';

import BatchSimulation from './stats/BatchSimulation';
import KeepRulesControl, { keepRulesSummary } from './stats/KeepRulesControl';
import ProbabilityCalculator from './stats/ProbabilityCalculator';
import SampleHandSimulator from './stats/SampleHandSimulator';
import TurnOptionsControl, { turnOptionsSummary } from './stats/TurnOptionsControl';

// ============================================================================
// Types
// ============================================================================

export interface DeckProbabilityPanelProps {
  cards: EnrichedDeckCard[];
  format: DeckFormat | null;
  onClose: () => void;
}

// ============================================================================
// Component
// ============================================================================

export default function DeckProbabilityPanel({ cards, format, onClose }: DeckProbabilityPanelProps): ReactElement {
  const [settings, setSettings] = useAtom(handSimSettingsAtom);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const rules = resolveKeepRules(settings, format);
  const turnOptions = resolveTurnOptions(settings);

  const handleRulesChange = (next: KeepRules): void => {
    setSettings({ ...settings, overrides: next });
  };

  const handleTurnOptionsChange = (next: TurnSimOptions): void => {
    setSettings({ ...settings, onThePlay: next.onThePlay, simTurns: next.turns });
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="flex shrink-0 items-center justify-between border-b border-zinc-800 px-4 py-3">
        <div className="flex items-center gap-2">
          <IoCalculator className="text-aether h-5 w-5" />
          <h2 className="text-sm font-semibold text-zinc-100">Probability Tools</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
          aria-label="Close probability panel"
        >
          <FaTimes className="h-4 w-4" />
        </button>
      </header>

      {/* Shared simulation settings */}
      <div className="shrink-0 border-b border-zinc-800 bg-zinc-900/40">
        <button
          type="button"
          onClick={() => setSettingsOpen((open) => !open)}
          aria-expanded={settingsOpen}
          aria-controls="sim-settings-body"
          className="flex w-full items-center gap-2 px-4 py-2 text-left transition-colors hover:bg-zinc-800/40 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
        >
          {settingsOpen ? (
            <IoChevronDown className="h-3 w-3 shrink-0 text-zinc-500" />
          ) : (
            <IoChevronForward className="h-3 w-3 shrink-0 text-zinc-500" />
          )}
          <IoOptions className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
          <span className="shrink-0 text-xs font-medium text-zinc-300">Simulation settings</span>
          <span className="text-ash/70 truncate text-[11px]">
            {keepRulesSummary(rules)} · {turnOptionsSummary(turnOptions)}
          </span>
        </button>
        {settingsOpen && (
          <div id="sim-settings-body" className="space-y-2 px-4 pt-1 pb-3">
            <KeepRulesControl
              rules={rules}
              format={format}
              onChange={handleRulesChange}
              onReset={() => setSettings({ ...settings, overrides: null })}
            />
            <div className="border-t border-zinc-800 pt-2">
              <TurnOptionsControl options={turnOptions} onChange={handleTurnOptionsChange} />
            </div>
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 space-y-1 overflow-y-auto">
        {/* Sample Hand Simulator */}
        <section className="border-b border-zinc-800 p-4">
          <div className="mb-3 flex items-center gap-2">
            <IoDice className="h-4 w-4 text-zinc-400" />
            <h3 className="text-xs font-semibold tracking-wider text-zinc-400 uppercase">Sample Hand</h3>
          </div>
          <SampleHandSimulator cards={cards} rules={rules} turnOptions={turnOptions} />
        </section>

        {/* Batch Simulation */}
        <section className="border-b border-zinc-800 p-4">
          <div className="mb-3 flex items-center gap-2">
            <IoStatsChart className="h-4 w-4 text-zinc-400" />
            <h3 className="text-xs font-semibold tracking-wider text-zinc-400 uppercase">Batch Simulation</h3>
          </div>
          <BatchSimulation cards={cards} rules={rules} onThePlay={turnOptions.onThePlay} />
        </section>

        {/* Probability Calculator */}
        <section className="p-4">
          <ProbabilityCalculator cards={cards} />
        </section>
      </div>
    </div>
  );
}
